// src/components/practice/tasks/ClozeMultipleChoice.jsx
import React from 'react';
import { marked } from 'marked';
import { FlagButton } from '../PartDisplay';
import { X } from 'lucide-react';

const ClozeMultipleChoice = ({
  partData,
  questions,
  examples,
  partAnswers,
  handleSingleAnswer,
  flaggedQuestions,
  toggleFlagQuestion,
  isMobileAnswerSheetOpen,
  onCloseMobileAnswerSheet,
  ...props
}) => {
    const { content } = partData;
    const mainText = content?.mainText || content?.text || '';
    const placeholderRegex = /(\[\d+\]|_\s\(\d+\)|_{5}\s\(\d+\)|\(\d+\))/g; // Added (\d+) to catch (0)
    const allProps = { flaggedQuestions, toggleFlagQuestion, ...props };
    const isGoetheC1Part1 = partData.examProvider === 'GOETHE' &&
                        partData.level === 'C1' &&
                        partData.partId === 'reading-part-1';
    // Answer sheet: single layout for all (sticky on lg), no separate scroll container, removes the middle scrollbar
// Parse the text for special formatting
let topic = '';
let subtitle = '';
let introParagraph = '';
let bodyText = mainText;

if (isGoetheC1Part1) {
  const lines = mainText.split('\n');
  if (lines.length >= 3) {
    topic = lines[0].replace(/\*\*/g, '').trim();
    subtitle = lines[1].replace(/\*\*/g, '').trim();
    introParagraph = lines[2].trim();
    bodyText = lines.slice(3).join('\n');
  }
}
    // --- LEFT COLUMN CONTENT (Reading) ---
    const leftContent = (
      <div className="space-y-6">


        {/* --- Examples block REMOVED from here --- */}

        {/* Main Text Box */}
        <div className="border-2 border-gray-900 bg-white p-6 rounded-none">
          {partData.content?.title && (
            <h3 className="text-xl font-bold text-gray-900 border-b-2 border-gray-300 pb-3 mb-4">
              {partData.content.title}
            </h3>
          )}
          {/* --- MODIFIED: Text with styled gaps --- */}
          <div className="prose prose-sm max-w-none text-gray-900 leading-relaxed whitespace-pre-wrap">
              {mainText.split(placeholderRegex).map((part, index) => {
                  if (!placeholderRegex.test(part)) {
                    // Regular text part
                    return <span key={`text-${index}`} dangerouslySetInnerHTML={{ __html: marked.parseInline(part, { breaks: true }) }} />;
                  }

                  // This is a placeholder
                  const blankNumber = part.match(/\d+/)[0];
                  const isExampleGap = blankNumber === '0' || (examples.length > 0 && examples[0].blankNumber === blankNumber);

                  if (isExampleGap) {
                    // --- THIS IS THE CHANGE ---
                    // We no longer show the answer text, just the blank number.
                    const exampleDisplay = `(${blankNumber})`;

                    return (
                      <span key={`blank-${index}`} className="inline-block text-center font-bold mx-1 px-3 py-1 bg-gray-100 border-2 border-gray-400 rounded-none">
                        {exampleDisplay}
                      </span>
                    );
                    // --- END CHANGE ---
                  }

                  // This is a regular question gap
                  return (
                      <span key={`blank-${index}`} className="inline-block text-center font-bold mx-1 px-3 py-1 bg-gray-100 border-2 border-gray-900 rounded-none">
                          ({blankNumber})
                      </span>
                  );
              })}
          </div>
        </div>
      </div>
    );

    // --- RIGHT COLUMN CONTENT (Answering) ---
    // (This section is unchanged and correctly shows the example answer)
    const answerSheet = (
      <div className="space-y-1">
        {/* Answer Sheet Header */}
        <div className="border-2 border-gray-900 bg-white p-3 rounded-none mb-4">
          <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wide">
            Antwortbogen
          </h3>
          <p className="text-xs text-gray-600 mt-1">
            Markieren Sie Ihre Antwort.
          </p>
        </div>

        {/* Examples List */}
        {examples.map(ex => {
          const correctAnswer = ex.correctAnswer;
          return (
            <div key={ex.questionId} className="border border-gray-900 bg-yellow-50 p-4 rounded-none opacity-90">
              {/* Example Header */}
              <div className="flex items-start justify-between mb-3">
                <p className="font-bold text-sm">
                  {ex.blankNumber || '0'}.
                </p>
                {/* No FlagButton */}
              </div>

              {/* Disabled Options List */}
              <div className="space-y-2 ml-6">
                {ex.options && ex.options.map((option, index) => (
                  <label
                    key={option.id}
                    className={`
                      flex items-start p-3 border-2 rounded-none transition-all cursor-not-allowed
                      ${correctAnswer === option.id
                        ? 'border-gray-900 bg-gray-900 text-white' // Selected
                        : 'border-gray-400 bg-white' // Default
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name={`example-${ex.questionId}`}
                      value={option.id}
                      checked={correctAnswer === option.id}
                      disabled
                      className="sr-only" // Hide the radio dot
                    />
                    <span className={`font-semibold w-6 flex-shrink-0 ${correctAnswer === option.id ? 'text-white' : 'text-gray-900'}`}>
                      {String.fromCharCode(97 + index)}.
                    </span>
                    <span className={`flex-1 ${correctAnswer === option.id ? 'text-white' : 'text-gray-900'}`}>
                      {option.text}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}

        {/* Regular Questions List */}
        {questions.map(question => {
          const currentAnswer = partAnswers[question.questionId];
          return (
            <div key={question.questionId} className="border border-t-0 border-gray-900 bg-white p-4 rounded-none hover:bg-gray-50">
              {/* Question Header (Unchanged) */}
              <div className="flex items-start justify-between mb-3">
                <p className="font-bold text-sm">
                  {question.blankNumber}.
                </p>
                <FlagButton {...allProps} questionId={question.questionId} />
              </div>

              {/* Options List (Unchanged) */}
              <div className="space-y-2 ml-6">
                {question.options && question.options.map((option, index) => (
                  <label
                    key={option.id}
                    className={`
                      flex items-start p-3 border-2 rounded-none transition-all cursor-pointer
                      ${currentAnswer === option.id
                        ? 'border-gray-900 bg-gray-900 text-white'
                        : 'border-gray-400 bg-white hover:border-gray-900 hover:shadow-md hover:-translate-y-0.5'
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name={question.questionId}
                      value={option.id}
                      checked={currentAnswer === option.id}
                      onChange={() => handleSingleAnswer(question.questionId, option.id)}
                      className="sr-only"
                    />
                    <span className={`font-semibold w-6 flex-shrink-0 ${currentAnswer === option.id ? 'text-white' : 'text-gray-900'}`}>
                      {String.fromCharCode(97 + index)}.
                    </span>
                    <span className={`flex-1 ${currentAnswer === option.id ? 'text-white' : 'text-gray-900'}`}>
                      {option.text}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );

    const mobileOverlay = (isMobileAnswerSheetOpen && onCloseMobileAnswerSheet) ? (
      <div className="fixed inset-0 z-[60] flex flex-col justify-end md:hidden" aria-modal="true" role="dialog" aria-label="Antwortbogen">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCloseMobileAnswerSheet} aria-hidden />
        <div className="relative bg-white rounded-t-2xl shadow-2xl max-h-[90vh] flex flex-col animate-slide-up">
          <div className="flex items-center justify-between flex-shrink-0 p-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
            <h3 className="text-lg font-bold text-gray-900">Antwortbogen</h3>
            <button type="button" onClick={onCloseMobileAnswerSheet} className="p-2 -m-2 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation min-w-[48px] min-h-[48px] flex items-center justify-center" aria-label="Schließen">
              <X className="w-6 h-6 text-gray-700" />
            </button>
          </div>
          <div className="overflow-y-auto overscroll-contain p-4 pb-safe">{answerSheet}</div>
        </div>
      </div>
    ) : null;

    return (
  <div className="w-full max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-6">
    <div className="grid grid-cols-1 lg:grid-cols-5 lg:gap-8">
      <div className="lg:col-span-3 min-w-0">
        {leftContent}
      </div>
      <div className="hidden lg:flex lg:col-span-2 mt-6 lg:mt-0 min-h-0 flex-col">
        <div className="lg:sticky lg:top-6 min-h-0 max-h-[60vh] sm:max-h-[65vh] lg:max-h-[calc(100vh-5rem)] overflow-y-auto pr-2 border-l border-gray-200">
          {answerSheet}
        </div>
      </div>
    </div>
    {mobileOverlay}
  </div>
);
};

export default ClozeMultipleChoice;